import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, List, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2, Clock, User, X, ChevronDown, Pencil, FileText, Eye } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths, isSameDay, startOfWeek, endOfWeek, addMinutes } from "date-fns";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import { useBusiness } from "@/hooks/use-business";
import { useAppointments } from "@/hooks/use-appointments";
import { useServices } from "@/hooks/use-services";
import { useStaff } from "@/hooks/use-staff";
import { useToast } from "@/hooks/use-toast";
import { useLocationContext } from "@/contexts/LocationContext";
import { cleanPhoneNumber, phoneToE164 } from "@/lib/phone-utils";
import { supabase } from "@/integrations/supabase/client";

// Currency helper
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    GHS: "₵",
    NGN: "₦",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

const formatPrice = (amount: number | string | null | undefined, currency: string = "GHS"): string => {
  if (!amount) return `${getCurrencySymbol(currency)}0.00`;
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return `${getCurrencySymbol(currency)}${numAmount.toFixed(2)}`;
};

export default function Appointments() {
  const { business, loading: businessLoading } = useBusiness();
  const { appointments, loading: appointmentsLoading, createAppointment, updateAppointment, refetch } = useAppointments();
  const { services } = useServices();
  const { staff } = useStaff();
  const { toast } = useToast();
  const { selectedLocationId } = useLocationContext();
  
  // Check if this is a clinic
  const isClinic = (business as any)?.primary_module === "clinics";
  const customerLabel = isClinic ? "Patient" : "Customer";
  
  const [view, setView] = useState<"list" | "calendar">("calendar");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [staffFilter, setStaffFilter] = useState("all");

  // Appointment creation dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [appointmentDate, setAppointmentDate] = useState<Date | undefined>(new Date());
  const [appointmentTime, setAppointmentTime] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<null | {
    id: string;
    code: string;
    discount_type: "percent" | "amount";
    discount_value: number;
  }>(null);
  const [countryCode, setCountryCode] = useState("+233");
  const [isCreating, setIsCreating] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(true);
  
  // Add-ons state
  const [serviceAddons, setServiceAddons] = useState<any[]>([]);
  const [addonQtyById, setAddonQtyById] = useState<Record<string, number>>({});
  const [addonsLoading, setAddonsLoading] = useState(false);

  // Appointment edit dialog state
  const [editingAppointment, setEditingAppointment] = useState<any>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editNotes, setEditNotes] = useState("");
  const [editingInspirationMedia, setEditingInspirationMedia] = useState<{ file_path: string; media_type: string }[]>([]);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
  const [viewingAppointment, setViewingAppointment] = useState<any>(null);
  const [viewingInspirationMedia, setViewingInspirationMedia] = useState<{ file_path: string; media_type: string }[]>([]);
  const [visitNotesMap, setVisitNotesMap] = useState<Record<string, boolean>>({});

  // Load visit notes for clinics
  useEffect(() => {
    if (!isClinic || appointments.length === 0) {
      setVisitNotesMap({});
      return;
    }

    const loadVisitNotes = async () => {
      try {
        const appointmentIds = appointments.map((apt) => apt.id);
        if (appointmentIds.length === 0) return;

        const { data: notes, error } = await (supabase as any)
          .from("visit_notes")
          .select("appointment_id")
          .in("appointment_id", appointmentIds);

        if (error) {
          console.error("Error loading visit notes:", error);
          return;
        }

        const notesMap: Record<string, boolean> = {};
        notes?.forEach((note: any) => {
          notesMap[note.appointment_id] = true;
        });
        setVisitNotesMap(notesMap);
      } catch (error) {
        console.error("Error loading visit notes:", error);
      }
    };

    loadVisitNotes();
  }, [isClinic, appointments]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  // Filter appointments
  const filteredAppointments = appointments.filter((apt) => {
    const matchesSearch = searchQuery === "" || 
      apt.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      apt.service?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || apt.status === statusFilter;
    const matchesService = serviceFilter === "all" || apt.service_id === serviceFilter;
    const matchesStaff = staffFilter === "all" || apt.staff_id === staffFilter;
    // Location filter: filter by appointment's location_id if selectedLocationId is set
    const matchesLocation = !selectedLocationId || apt.location_id === selectedLocationId;
    return matchesSearch && matchesStatus && matchesService && matchesStaff && matchesLocation;
  });

  // Get appointments for selected date
  const selectedDateAppointments = selectedDate 
    ? filteredAppointments.filter((apt) => isSameDay(new Date(apt.start_datetime), selectedDate))
    : [];

  // Get appointment counts per day for calendar view
  const getAppointmentCountForDay = (day: Date) => {
    return filteredAppointments.filter((apt) => isSameDay(new Date(apt.start_datetime), day)).length;
  };

  const isLoading = businessLoading || appointmentsLoading;

  // Generate time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Toggle service selection
  const toggleService = (serviceId: string) => {
    setSelectedServices(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  // Reset form
  const resetAppointmentForm = () => {
    setSelectedServices([]);
    setAppointmentDate(new Date());
    setAppointmentTime("");
    setSelectedStaffId("");
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setCouponCode("");
    setCouponError(null);
    setAppliedCoupon(null);
    setCountryCode("+233");
    setEditNotes("");
    setEditingInspirationMedia([]);
    setServiceAddons([]);
    setAddonQtyById({});
  };

  const handleApplyCoupon = async () => {
    if (!business) {
      setCouponError("Business not loaded");
      return;
    }
    const code = couponCode.trim().toUpperCase();
    setCouponError(null);
    if (!code) {
      setAppliedCoupon(null);
      return;
    }

    if (selectedServices.length !== 1) {
      setAppliedCoupon(null);
      setCouponError("Select a single service to apply a coupon.");
      return;
    }

    const serviceId = selectedServices[0];

    setCouponApplying(true);
    try {
      const { data, error } = await (supabase as any).rpc("validate_business_coupon_public", {
        p_business_id: business.id,
        p_code: code,
        p_service_id: serviceId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.ok) {
        setAppliedCoupon(null);
        setCouponError(String(row?.error || "Invalid coupon code"));
        return;
      }

      setAppliedCoupon({
        id: String(row.coupon_id),
        code: String(row.code || code),
        discount_type: row.discount_type === "amount" ? "amount" : "percent",
        discount_value: Number(row.discount_value || 0),
      });
      setCouponError(null);
    } catch (e: any) {
      console.error("Coupon validation failed:", e);
      setAppliedCoupon(null);
      const msg = String(e?.message || "Failed to apply coupon");
      setCouponError(
        msg.toLowerCase().includes("could not find")
          ? "Coupons are not enabled for this business yet."
          : msg
      );
    } finally {
      setCouponApplying(false);
    }
  };

  // Fetch add-ons for selected services
  useEffect(() => {
    const fetchAddons = async () => {
      if (!business?.id || selectedServices.length === 0) {
        setServiceAddons([]);
        setAddonQtyById({});
        return;
      }

      try {
        setAddonsLoading(true);
        const { data, error } = await supabase
          .from("service_addons")
          .select("*")
          .eq("business_id", business.id)
          .in("service_id", selectedServices)
          .eq("active", true)
          .order("name");

        if (error) throw error;
        setServiceAddons(data || []);
        // Reset quantities when services change
        setAddonQtyById({});
      } catch (err) {
        console.error("Error fetching add-ons:", err);
        setServiceAddons([]);
      } finally {
        setAddonsLoading(false);
      }
    };

    fetchAddons();
  }, [selectedServices, business?.id]);

  // Open edit dialog and populate form
  const openEditDialog = (apt: any) => {
    setEditingAppointment(apt);
    // Set service (single service for edit)
    setSelectedServices(apt.service_id ? [apt.service_id] : []);
    // Set date and time
    const startDate = new Date(apt.start_datetime);
    setAppointmentDate(startDate);
    const timeStr = format(startDate, "HH:mm");
    setAppointmentTime(timeStr);
    // Set staff
    setSelectedStaffId(apt.staff_id || "");
    // Set customer info
    setCustomerName(apt.customer?.name || "");
    const phone = apt.customer?.phone || "";
    const digits = cleanPhoneNumber(phone, "233") || String(phone).replace(/\D/g, "");
    const candidates = [
      { cc: "254", ui: "+254" },
      { cc: "234", ui: "+234" },
      { cc: "233", ui: "+233" },
      { cc: "44", ui: "+44" },
      { cc: "1", ui: "+1" },
    ];
    const hit = candidates.find((c) => digits.startsWith(c.cc));
    setCountryCode(hit?.ui || "+233");
    setCustomerPhone(hit ? digits.slice(hit.cc.length) : digits);
    setCustomerEmail(apt.customer?.email || "");
    setEditNotes(apt.notes || "");
    setEditingInspirationMedia([]);
    setIsEditDialogOpen(true);
    // Fetch client inspiration media for this appointment
    (supabase as any)
      .from("appointment_inspiration_media")
      .select("file_path, media_type")
      .eq("appointment_id", apt.id)
      .then(({ data }: { data: { file_path: string; media_type: string }[] | null }) => setEditingInspirationMedia(data || []));
  };

  const openViewDetails = (apt: any) => {
    setViewingAppointment(apt);
    setViewingInspirationMedia([]);
    setIsViewDetailsOpen(true);
    (supabase as any)
      .from("appointment_inspiration_media")
      .select("file_path, media_type")
      .eq("appointment_id", apt.id)
      .then(({ data }: { data: { file_path: string; media_type: string }[] | null }) => setViewingInspirationMedia(data || []));
  };

  // Handle appointment update
  const handleUpdateAppointment = async () => {
    if (!editingAppointment || !business) return;

    if (selectedServices.length === 0) {
      toast({
        title: "Error",
        description: "Please select a service",
        variant: "destructive",
      });
      return;
    }

    if (!appointmentDate) {
      toast({
        title: "Error",
        description: "Please select a date",
        variant: "destructive",
      });
      return;
    }

    if (!appointmentTime) {
      toast({
        title: "Error",
        description: "Please select a time",
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);
    try {
      // Calculate new start and end datetime
      const [hours, minutes] = appointmentTime.split(":").map(Number);
      const startDateTime = new Date(appointmentDate);
      startDateTime.setHours(hours, minutes, 0, 0);
      
      const selectedService = services.find(s => s.id === selectedServices[0]);
      if (!selectedService) throw new Error("Service not found");
      
      const endDateTime = addMinutes(startDateTime, selectedService.duration_minutes || 30);

      // Update customer info if changed
      const customerId = editingAppointment.customer_id;
      const normalizedEmail = (customerEmail || "").trim().toLowerCase();
      const cleanedPhone = phoneToE164(`${countryCode}${customerPhone}`, countryCode.replace("+", ""));
      
      if (customerName || normalizedEmail || cleanedPhone) {
        const customerUpdates: any = {};
        if (customerName) customerUpdates.name = customerName;
        if (normalizedEmail) customerUpdates.email = normalizedEmail;
        if (cleanedPhone) customerUpdates.phone = cleanedPhone;
        
        if (Object.keys(customerUpdates).length > 0) {
          await supabase
            .from("customers")
            .update(customerUpdates)
            .eq("id", customerId);
        }
      }

      // Update appointment
      await updateAppointment(editingAppointment.id, {
        service_id: selectedServices[0],
        staff_id: selectedStaffId || null,
        start_datetime: startDateTime.toISOString(),
        end_datetime: endDateTime.toISOString(),
        notes: editNotes.trim() || null,
      });

      toast({
        title: "Success",
        description: "Appointment updated successfully",
        variant: "success",
      });

      // Wait a moment for database to commit, then send email notification to customer
      setTimeout(() => {
        supabase.functions
          .invoke("send-email", {
            body: {
              type: "appointment_updated",
              appointmentId: editingAppointment.id,
            },
          })
          .then(({ data, error }) => {
            if (error) {
              console.error("send-email appointment_updated failed:", error);
            } else {
              console.log("Appointment updated email sent:", data);
            }
          })
          .catch((e) => console.error("send-email appointment_updated crashed:", e));
      }, 1000);

      setIsEditDialogOpen(false);
      setEditingAppointment(null);
      resetAppointmentForm();
      await refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update appointment",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Create or find customer
  const findOrCreateCustomer = async () => {
    if (!business) throw new Error("No business found");

    const normalizedEmail = (customerEmail || "").trim().toLowerCase();
    const cleanedPhone = phoneToE164(`${countryCode}${customerPhone}`, countryCode.replace("+", ""));

    // Prefer email as the unique identifier to avoid duplicates
    if (normalizedEmail) {
      const { data: existingByEmail } = await supabase
        .from("customers")
        .select("*")
        .eq("business_id", business.id)
        .eq("email", normalizedEmail)
        .maybeSingle();

      if (existingByEmail) {
        // Update customer info if provided
        const updates: any = {};
        if (customerName && customerName !== existingByEmail.name) updates.name = customerName;
        if (cleanedPhone && cleanedPhone !== existingByEmail.phone) updates.phone = cleanedPhone;
        if (Object.keys(updates).length > 0) {
          await supabase.from("customers").update(updates).eq("id", existingByEmail.id);
        }
        return existingByEmail.id;
      }
    }

    // Fallback: try to find existing customer by phone (in case email not provided)
    if (cleanedPhone) {
      const { data: existingCustomer } = await supabase
        .from("customers")
        .select("*")
        .eq("business_id", business.id)
        .eq("phone", cleanedPhone)
        .maybeSingle();

      if (existingCustomer) {
        // Update customer info if provided
        if (customerName && customerName !== existingCustomer.name) {
          await supabase
            .from("customers")
            .update({ name: customerName, email: normalizedEmail || null })
            .eq("id", existingCustomer.id);
        }
        return existingCustomer.id;
      }
    }

    // Create new customer
    const { data: newCustomer, error } = await supabase
      .from("customers")
      .upsert({
        business_id: business.id,
        name: customerName,
        phone: cleanedPhone,
        email: normalizedEmail || null,
      }, { onConflict: "business_id,email" })
      .select()
      .single();

    if (error) throw error;
    return newCustomer.id;
  };

  // Handle appointment creation
  const handleCreateAppointment = async () => {
    if (!business) {
      toast({
        title: "Error",
        description: "No business found",
        variant: "destructive",
      });
      return;
    }

    if (selectedServices.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one service",
        variant: "destructive",
      });
      return;
    }

    if (!appointmentDate) {
      toast({
        title: "Error",
        description: "Please select a date",
        variant: "destructive",
      });
      return;
    }

    if (!appointmentTime) {
      toast({
        title: "Error",
        description: "Please select a time",
        variant: "destructive",
      });
      return;
    }

    if (!customerName.trim()) {
      toast({
        title: "Error",
        description: `${customerLabel} name is required`,
        variant: "destructive",
      });
      return;
    }

    if (!customerPhone.trim()) {
      toast({
        title: "Error",
        description: "Customer phone is required",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      // Create or find customer
      const customerId = await findOrCreateCustomer();

      // Create appointments for each selected service
      const selectedServiceObjects = services.filter(s => selectedServices.includes(s.id));
      
      for (let i = 0; i < selectedServiceObjects.length; i++) {
        const service = selectedServiceObjects[i];
        
        // Calculate start and end datetime
        const [hours, minutes] = appointmentTime.split(":").map(Number);
        const startDateTime = new Date(appointmentDate);
        startDateTime.setHours(hours, minutes, 0, 0);
        
        // If this is not the first service, add buffer time (15 minutes between services)
        if (i > 0) {
          startDateTime.setMinutes(startDateTime.getMinutes() + (i * 15));
        }

        const endDateTime = addMinutes(startDateTime, service.duration_minutes || 30);

        // Calculate pricing, add-ons, and optional coupon discount for this service
        const serviceBasePrice = Number((service as any)?.price_amount || 0);
        const serviceCurrency = (service as any)?.currency || business?.currency || "GHS";
        const serviceAddonsForThisService = serviceAddons.filter(a => a.service_id === service.id);
        const selectedAddonsForService = serviceAddonsForThisService.filter(a => (addonQtyById[a.id] || 0) > 0);

        const addonsTotal = selectedAddonsForService.reduce((sum, addon) => {
          const qty = addonQtyById[addon.id] || 0;
          const price = Number(addon.price_amount || 0);
          return sum + price * qty;
        }, 0);

        const subtotal = serviceBasePrice + addonsTotal;

        let totalPriceAmount = subtotal;
        let discountAmount = 0;
        let couponId: string | null = null;
        let couponCodeValue: string | null = null;

        if (appliedCoupon && selectedServiceObjects.length === 1 && service.id === selectedServiceObjects[0].id) {
          const requiresDeposit = !!(service as any)?.requires_deposit;
          const depositAmount = requiresDeposit ? Number((service as any)?.deposit_amount || 0) : 0;
          const couponFloor = requiresDeposit ? Math.max(0, depositAmount) : 0;

          const rawDiscount =
            appliedCoupon.discount_type === "percent"
              ? (subtotal * Number(appliedCoupon.discount_value || 0)) / 100
              : Number(appliedCoupon.discount_value || 0);

          const discountCap = Math.max(0, subtotal - couponFloor);
          discountAmount = Math.min(Math.max(0, rawDiscount), discountCap);
          totalPriceAmount = Math.max(0, subtotal - discountAmount);
          couponId = appliedCoupon.id;
          couponCodeValue = appliedCoupon.code;
        }

        const createdAppointment = await createAppointment({
          customer_id: customerId,
          service_id: service.id,
          staff_id: selectedStaffId || null,
          location_id: selectedLocationId || null,
          start_datetime: startDateTime.toISOString(),
          end_datetime: endDateTime.toISOString(),
          status: "confirmed",
          source: "admin",
          total_price_amount: Number(totalPriceAmount.toFixed(2)),
          // The following fields exist in the DB but may not be in generated types
          ...(discountAmount
            ? ({
                discount_amount: Number(discountAmount.toFixed(2)),
              } as any)
            : {}),
          ...(couponId || couponCodeValue
            ? ({
                coupon_id: couponId,
                coupon_code: couponCodeValue,
              } as any)
            : {}),
          ...(serviceCurrency
            ? ({
                currency: serviceCurrency,
              } as any)
            : {}),
        } as any);

        // Save add-ons for this appointment (only add-ons for this specific service)
        if (createdAppointment?.id) {
          if (selectedAddonsForService.length > 0) {
            const payload = selectedAddonsForService.map((a) => ({
              appointment_id: createdAppointment.id,
              addon_id: a.id,
              name: a.name,
              price_amount: a.price_amount,
              quantity: addonQtyById[a.id] || 1,
              currency: a.currency || service.currency || business?.currency || "GHS",
            }));
            
            const { error: addErr } = await supabase.from("appointment_addons").insert(payload as any);
            if (addErr) {
              console.error("Failed to save appointment add-ons:", addErr);
              // Don't throw - appointment is created, add-ons are optional
            }
          }
        }
      }

      toast({
        title: "Success",
        description: `${selectedServices.length} appointment(s) created successfully`,
        variant: "success",
      });

      setIsCreateDialogOpen(false);
      resetAppointmentForm();
      await refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create appointment",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Appointments"
        action={
          <Button size="pill" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Appointment
          </Button>
        }
      />

      {/* View Toggle and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-muted rounded-lg p-1">
            <button
              onClick={() => setView("list")}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                view === "list"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <List className="h-4 w-4" />
              List
            </button>
            <button
              onClick={() => setView("calendar")}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                view === "calendar"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <CalendarIcon className="h-4 w-4" />
              Calendar
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search appointments..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staff.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Calendar View */}
          {view === "calendar" && (
            <div className="grid lg:grid-cols-[380px_1fr] gap-6">
              {/* Calendar - Compact */}
              <div className="bg-card rounded-xl border border-border shadow-soft p-5">
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={goToPreviousMonth}
                    className="p-1.5 rounded-lg hover:bg-accent transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <h2 className="text-base font-semibold text-foreground">
                    {format(currentMonth, "MMMM yyyy")}
                  </h2>
                  <button
                    onClick={goToNextMonth}
                    className="p-1.5 rounded-lg hover:bg-accent transition-colors"
                  >
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs font-medium text-muted-foreground py-1.5"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day) => {
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isCurrentDay = isToday(day);
                    const appointmentCount = getAppointmentCountForDay(day);

                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        className={cn(
                          "aspect-square flex flex-col items-center justify-start pt-2 rounded-md text-sm font-medium transition-colors relative min-h-[42px]",
                          !isCurrentMonth && "text-muted-foreground/50",
                          isCurrentMonth && !isSelected && "hover:bg-accent",
                          isCurrentDay && !isSelected && "bg-accent text-primary font-semibold",
                          isSelected && "bg-primary text-primary-foreground"
                        )}
                      >
                        <span className="leading-none">{format(day, "d")}</span>
                        {appointmentCount > 0 && (
                          <span
                            className={cn(
                              "absolute bottom-1 left-1/2 -translate-x-1/2 inline-flex items-center justify-center",
                              "h-4 min-w-4 px-1 rounded-full text-[9px] font-semibold leading-none",
                              isSelected
                                ? "bg-primary-foreground text-primary"
                                : "bg-primary/10 text-primary border border-primary/20"
                            )}
                            aria-label={`${appointmentCount} booking${appointmentCount === 1 ? "" : "s"}`}
                          >
                            {appointmentCount > 9 ? "9+" : appointmentCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected Day Details */}
              <div className="bg-card rounded-xl border border-border shadow-soft p-6">
                {selectedDate ? (
                  <>
                    <div className="mb-5">
                      <h3 className="text-lg font-semibold text-foreground">
                      {format(selectedDate, "EEEE, MMMM d")}
                    </h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {selectedDateAppointments.length} {selectedDateAppointments.length === 1 ? 'appointment' : 'appointments'}
                      </p>
                    </div>
                    {selectedDateAppointments.length > 0 ? (
                      <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
                        {selectedDateAppointments.map((apt) => (
                          <div
                            key={apt.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => openViewDetails(apt)}
                            onKeyDown={(e) => e.key === "Enter" && openViewDetails(apt)}
                            className="p-4 rounded-lg border border-border bg-background hover:bg-accent/30 transition-colors cursor-pointer"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="font-medium text-foreground mb-1">{apt.service?.name}</h4>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Clock className="h-3.5 w-3.5" />
                                  <span>{format(new Date(apt.start_datetime), "h:mm a")}</span>
                                </div>
                              </div>
                              <StatusBadge status={apt.status} />
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t border-border/50">
                              <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                                  {apt.customer?.name?.charAt(0) || "?"}
                                </div>
                                <span className="text-sm text-foreground">{apt.customer?.name || "Unknown"}</span>
                              </div>
                              {isClinic && visitNotesMap[apt.id] && (
                                <Button variant="outline" size="sm" asChild className="h-7 px-2 text-xs">
                                  <Link to={`/app/visit-notes/${apt.id}`} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                    <FileText className="h-3 w-3 mr-1" />
                                    Notes
                                  </Link>
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState
                        icon={CalendarIcon}
                        title="No appointments"
                        description="No appointments scheduled for this day"
                      />
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <div className="h-16 w-16 rounded-2xl bg-accent/50 flex items-center justify-center mb-4">
                      <CalendarIcon className="h-8 w-8 text-muted-foreground/60" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Select a date to view appointments</h3>
                    <p className="text-sm text-muted-foreground max-w-xs">
                      Days with appointments are marked with colored indicators showing the number of appointments by status
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* List View */}
          {view === "list" && (
            <div className="bg-card rounded-xl border border-border shadow-soft overflow-hidden">
              {filteredAppointments.length > 0 ? (
                <div className="divide-y divide-border">
                  {filteredAppointments.map((apt) => (
                    <div key={apt.id} className="p-4 sm:p-6 hover:bg-accent/30 transition-colors">
                      {/* Layout: Left (Info) | Right (Date/Time + Status) */}
                      <div className="flex items-start justify-between gap-4">
                        {/* Left Side: Customer & Service Info */}
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div>
                            <h3 className="font-semibold text-base sm:text-lg text-foreground leading-tight">
                              {apt.customer?.name || "Unknown"}
                            </h3>
                            <p className="text-sm sm:text-base text-muted-foreground mt-0.5 leading-tight">
                              {apt.service?.name}
                            </p>
                          </div>
                          
                          {/* Price */}
                          <p className="text-sm sm:text-base font-medium text-foreground">
                            {formatPrice(
                              (apt as any).total_price_amount ?? apt.service?.price_amount ?? 0,
                              (apt as any).currency || apt.service?.currency || business?.currency || "GHS"
                            )}
                          </p>
                        </div>
                        
                        {/* Right Side: Date, Time, Status & Edit (stacked vertically) */}
                        <div className="flex flex-col items-end gap-3 flex-shrink-0">
                          {/* Date & Time */}
                          <div className="text-right space-y-0.5">
                            <div className="flex flex-col">
                              <p className="text-sm sm:text-base font-medium text-foreground">
                                {format(new Date(apt.start_datetime), "MMM d,")}
                              </p>
                              <p className="text-sm sm:text-base font-medium text-foreground">
                                {format(new Date(apt.start_datetime), "yyyy")}
                              </p>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {format(new Date(apt.start_datetime), "h:mm a")}
                            </p>
                          </div>
                          
                          {/* Status Selector & Edit Button */}
                          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                            {isClinic && visitNotesMap[apt.id] && (
                              <Button
                                variant="outline"
                                size="sm"
                                asChild
                                className="h-8 px-2 text-xs"
                              >
                                <Link to={`/app/visit-notes/${apt.id}?view=true`}>
                                  <FileText className="h-3.5 w-3.5 mr-1" />
                                  View Notes
                                </Link>
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 flex-shrink-0"
                              onClick={() => openViewDetails(apt)}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 flex-shrink-0"
                              onClick={() => openEditDialog(apt)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <div className="flex-shrink-0 flex-1 sm:flex-initial">
                              <Select
                                value={apt.status}
                                onValueChange={async (newStatus) => {
                                  try {
                                    await updateAppointment(apt.id, { status: newStatus as any });
                                    toast({
                                      title: "Status updated",
                                      description: `Appointment status changed to ${newStatus}`,
                                    });

                                    // Fire-and-forget email notification (don't block UI if it fails)
                                    supabase.functions
                                      .invoke("send-email", {
                                        body: {
                                          type: "status_changed",
                                          appointmentId: apt.id,
                                          newStatus,
                                        },
                                      })
                                      .then(({ error }) => {
                                        if (error) {
                                          console.warn("send-email status_changed failed:", error);
                                        }
                                      })
                                      .catch((e) => console.warn("send-email status_changed crashed:", e));
                                  } catch (error) {
                                    toast({
                                      title: "Error",
                                      description: error instanceof Error ? error.message : "Failed to update status",
                                      variant: "destructive",
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger className="w-full sm:w-[140px] h-auto py-2 px-3 min-h-[36px]">
                                  <SelectValue>
                                    <StatusBadge status={apt.status} />
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="confirmed">Confirmed</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                  <SelectItem value="no_show">No Show</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Reschedule metadata (if present) - full width below on mobile */}
                      {(apt as any).rescheduled_from_start_datetime && (
                        <div className="mt-3 rounded-lg border border-border bg-accent/40 p-3 text-xs text-muted-foreground">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                            <span className="font-medium text-foreground">Rescheduled</span>
                            <span className="text-muted-foreground">
                              {format(new Date((apt as any).rescheduled_at || apt.updated_at), "MMM d, h:mm a")}
                            </span>
                          </div>

                          <div className="mt-2 space-y-1">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                              <span>From</span>
                              <span className="text-foreground sm:text-right">
                                {format(new Date((apt as any).rescheduled_from_start_datetime), "MMM d, yyyy h:mm a")}
                              </span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                              <span>To</span>
                              <span className="text-foreground sm:text-right">
                                {format(new Date(apt.start_datetime), "MMM d, yyyy h:mm a")}
                              </span>
                            </div>
                            {(apt as any).reschedule_reason && (
                              <div className="pt-1">
                                <span className="font-medium text-foreground">Reason:</span>{" "}
                                {(apt as any).reschedule_reason}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={CalendarIcon}
                  title="No appointments yet"
                  description="Your appointments will appear here once customers start booking"
                  action={{
                    label: "Add Appointment",
                    onClick: () => setIsCreateDialogOpen(true),
                  }}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Create Appointment Dialog */}
      <Dialog 
        open={isCreateDialogOpen} 
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            resetAppointmentForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-[900px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>{isClinic ? "Schedule New Visit" : "Create New Appointment"}</DialogTitle>
            <p className="text-xs text-muted-foreground">
              Book an appointment for a customer. You can select multiple services.
            </p>
          </DialogHeader>

          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column - Appointment Details */}
            <div className="space-y-5">
              {/* Service Selection */}
              <Collapsible open={isServicesOpen} onOpenChange={setIsServicesOpen}>
                <div className="space-y-2">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between cursor-pointer">
                      <Label className="cursor-pointer">Service *</Label>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isServicesOpen ? "rotate-180" : ""}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border border-border rounded-lg p-4 bg-background min-h-[120px] max-h-[240px] overflow-y-auto">
                      {services.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No services available</p>
                      ) : (
                        <div className="space-y-2">
                          {services.map((service) => (
                            <div key={service.id} className="flex items-center space-x-3 p-2 rounded-md hover:bg-accent/50 transition-colors">
                              <Checkbox
                                id={`service-${service.id}`}
                                checked={selectedServices.includes(service.id)}
                                onCheckedChange={() => toggleService(service.id)}
                              />
                              <Label
                                htmlFor={`service-${service.id}`}
                                className="flex-1 cursor-pointer text-sm font-medium"
                              >
                                {service.name}
                                <span className="text-muted-foreground ml-2 font-normal">
                                  ({service.duration_minutes} min)
                                </span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                  {selectedServices.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedServices.map((serviceId) => {
                        const service = services.find(s => s.id === serviceId);
                        return service ? (
                          <Badge key={serviceId} variant="secondary" className="gap-1.5 py-1.5 px-2.5">
                            {service.name}
                            <button
                              type="button"
                              onClick={() => toggleService(serviceId)}
                              className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </Collapsible>

              {/* Add-ons Selection */}
              {selectedServices.length > 0 && (
                <div className="space-y-2">
                  <Label>Add-ons (Optional)</Label>
                  {addonsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : serviceAddons.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">No add-ons available for selected services</p>
                  ) : (
                    <div className="border border-border rounded-lg p-4 bg-background space-y-3 max-h-[240px] overflow-y-auto">
                      {serviceAddons.map((addon) => {
                        const qty = addonQtyById[addon.id] || 0;
                        const maxQty = Math.max(1, Number((addon as any)?.max_quantity ?? 1));
                        const isToggleStyle = maxQty <= 1;
                        const addonService = services.find(s => s.id === addon.service_id);
                        return (
                          <div key={addon.id} className="flex items-start justify-between gap-3 p-2 rounded-md hover:bg-accent/50 transition-colors">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id={`addon-${addon.id}`}
                                  checked={qty > 0}
                                  onCheckedChange={(checked) => {
                                    setAddonQtyById(prev => ({
                                      ...prev,
                                      [addon.id]: checked ? 1 : 0,
                                    }));
                                  }}
                                />
                                <Label htmlFor={`addon-${addon.id}`} className="cursor-pointer flex-1">
                                  <div className="font-medium text-sm text-foreground">{addon.name}</div>
                                  {addonService && (
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                      For: {addonService.name}
                                    </div>
                                  )}
                                  {addon.description && (
                                    <div className="text-xs text-muted-foreground mt-0.5">{addon.description}</div>
                                  )}
                                </Label>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-sm font-medium text-foreground">
                                {formatPrice(addon.price_amount, addon.currency || business?.currency || "GHS")}
                              </span>
                              {qty > 0 && !isToggleStyle && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddonQtyById(prev => ({
                                        ...prev,
                                        [addon.id]: Math.max(0, (prev[addon.id] || 0) - 1),
                                      }));
                                    }}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                  <span className="text-sm font-medium text-foreground min-w-[20px] text-center">
                                    {qty}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    disabled={qty >= maxQty}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddonQtyById(prev => ({
                                        ...prev,
                                        [addon.id]: Math.min(maxQty, (prev[addon.id] || 0) + 1),
                                      }));
                                    }}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Date Selection */}
              <div className="space-y-2">
                <Label>Date *</Label>
                <div className="border border-border rounded-lg p-3 bg-background w-full">
                  <Calendar
                    mode="single"
                    selected={appointmentDate}
                    onSelect={(date) => {
                      setAppointmentDate(date);
                      setAppointmentTime(""); // Reset time when date changes
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    className="rounded-md w-full [&_.rdp]:w-full [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_table]:w-full [&_thead]:w-full [&_tbody]:w-full [&_tr]:grid [&_tr]:grid-cols-7 [&_tr]:w-full [&_td]:flex [&_td]:justify-center [&_th]:flex [&_th]:justify-center [&_.rdp-cell]:flex-1 [&_.rdp-day]:w-full [&_.rdp-day]:h-12 [&_.rdp-day]:text-base"
                  />
                </div>
              </div>

              {/* Time Selection */}
              {appointmentDate && (
                <div className="space-y-2">
                  <Label>Time *</Label>
                  <div className="bg-card rounded-lg border border-border p-4">
                    <h4 className="text-sm font-medium text-foreground mb-3">
                      Available times for {format(appointmentDate, "EEEE, MMMM d")}
                    </h4>
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[200px] overflow-y-auto">
                      {timeSlots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setAppointmentTime(time)}
                          className={cn(
                            "py-2 px-3 rounded-lg text-sm font-medium transition-colors",
                            appointmentTime === time
                              ? "bg-primary text-primary-foreground"
                              : "bg-accent hover:bg-accent/80 text-foreground"
                          )}
                        >
                          {format(new Date(`2000-01-01T${time}`), "h:mm a")}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Staff Selection */}
              <div className="space-y-2">
                <Label>Staff (Optional)</Label>
                <Select value={selectedStaffId || "any"} onValueChange={(value) => setSelectedStaffId(value === "any" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any available staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any available staff</SelectItem>
                    {staff.filter(s => s.active).map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Right Column - Customer Information */}
            <div className="space-y-5">
              {/* Customer Name */}
              <div className="space-y-2">
                <Label htmlFor="customer-name">{customerLabel} Name *</Label>
                <Input
                  id="customer-name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                />
              </div>

              {/* Customer Phone */}
              <div className="space-y-2">
                <Label htmlFor="customer-phone">Customer Phone *</Label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+233">GH +233</SelectItem>
                      <SelectItem value="+1">US +1</SelectItem>
                      <SelectItem value="+44">UK +44</SelectItem>
                      <SelectItem value="+234">NG +234</SelectItem>
                      <SelectItem value="+254">KE +254</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="customer-phone"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="Phone number"
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Customer Email */}
              <div className="space-y-2">
                <Label htmlFor="customer-email">Customer Email (Optional)</Label>
                <Input
                  id="customer-email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </div>

              {/* Coupon (optional, single-service only) */}
              {selectedServices.length === 1 ? (
                <div className="space-y-2">
                  <Label>Coupon (Optional)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError(null);
                      }}
                      placeholder="Enter coupon code"
                      className="h-9"
                      disabled={couponApplying}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="h-9"
                      disabled={couponApplying || !couponCode.trim()}
                      onClick={handleApplyCoupon}
                    >
                      {couponApplying ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Applying…
                        </>
                      ) : (
                        "Apply"
                      )}
                    </Button>
                    {appliedCoupon ? (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-9"
                        onClick={() => {
                          setAppliedCoupon(null);
                          setCouponCode("");
                          setCouponError(null);
                        }}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                  {couponError ? (
                    <p className="text-xs text-destructive mt-1">{couponError}</p>
                  ) : appliedCoupon ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Applied: <span className="font-semibold text-foreground">{appliedCoupon.code}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      Optional. Apply a coupon if this walk-in has a code.
                    </p>
                  )}
                </div>
              ) : selectedServices.length > 1 ? (
                <p className="text-xs text-muted-foreground">
                  Coupons can only be applied when a single service is selected. Multi-service coupons are handled on
                  the public booking page.
                </p>
              ) : null}
            </div>
          </div>
          </div>

          <DialogFooter className="px-6 pb-6 pt-4 border-t flex-shrink-0 gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetAppointmentForm();
              }}
              disabled={isCreating}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button onClick={handleCreateAppointment} disabled={isCreating} className="flex-1">
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Appointment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog 
        open={isEditDialogOpen} 
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingAppointment(null);
            resetAppointmentForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-[900px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Edit Appointment</DialogTitle>
            <p className="text-xs text-muted-foreground">
              Update appointment details. You can change the service, date, time, staff, and customer information.
            </p>
          </DialogHeader>

          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column - Appointment Details */}
            <div className="space-y-5">
              {/* Service Selection */}
              <Collapsible open={isServicesOpen} onOpenChange={setIsServicesOpen}>
                <div className="space-y-2">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between cursor-pointer">
                      <Label className="cursor-pointer">Service *</Label>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isServicesOpen ? "rotate-180" : ""}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border border-border rounded-lg p-4 bg-background min-h-[120px] max-h-[240px] overflow-y-auto">
                      {services.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No services available</p>
                      ) : (
                        <div className="space-y-2">
                          {services.map((service) => (
                            <div key={service.id} className="flex items-center space-x-3 p-2 rounded-md hover:bg-accent/50 transition-colors">
                              <Checkbox
                                id={`edit-service-${service.id}`}
                                checked={selectedServices.includes(service.id)}
                                onCheckedChange={() => {
                                  // For edit, only allow one service
                                  setSelectedServices([service.id]);
                                }}
                              />
                              <Label
                                htmlFor={`edit-service-${service.id}`}
                                className="flex-1 cursor-pointer text-sm font-medium"
                              >
                                {service.name}
                                <span className="text-muted-foreground ml-2 font-normal">
                                  ({service.duration_minutes} min)
                                </span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                  {selectedServices.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedServices.map((serviceId) => {
                        const service = services.find(s => s.id === serviceId);
                        return service ? (
                          <Badge key={serviceId} variant="secondary" className="gap-1.5 py-1.5 px-2.5">
                            {service.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </Collapsible>

              {/* Date Selection */}
              <div className="space-y-2">
                <Label>Date *</Label>
                <div className="border border-border rounded-lg p-3 bg-background w-full">
                  <Calendar
                    mode="single"
                    selected={appointmentDate}
                    onSelect={(date) => {
                      setAppointmentDate(date);
                      setAppointmentTime(""); // Reset time when date changes
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    className="rounded-md w-full [&_.rdp]:w-full [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_table]:w-full [&_thead]:w-full [&_tbody]:w-full [&_tr]:grid [&_tr]:grid-cols-7 [&_tr]:w-full [&_td]:flex [&_td]:justify-center [&_th]:flex [&_th]:justify-center [&_.rdp-cell]:flex-1 [&_.rdp-day]:w-full [&_.rdp-day]:h-12 [&_.rdp-day]:text-base"
                  />
                </div>
              </div>

              {/* Time Selection */}
              {appointmentDate && (
                <div className="space-y-2">
                  <Label>Time *</Label>
                  <div className="bg-card rounded-lg border border-border p-4">
                    <h4 className="text-sm font-medium text-foreground mb-3">
                      Available times for {format(appointmentDate, "EEEE, MMMM d")}
                    </h4>
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[200px] overflow-y-auto">
                      {timeSlots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setAppointmentTime(time)}
                          className={cn(
                            "py-2 px-3 rounded-lg text-sm font-medium transition-colors",
                            appointmentTime === time
                              ? "bg-primary text-primary-foreground"
                              : "bg-accent hover:bg-accent/80 text-foreground"
                          )}
                        >
                          {format(new Date(`2000-01-01T${time}`), "h:mm a")}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Staff Selection */}
              <div className="space-y-2">
                <Label>Staff (Optional)</Label>
                <Select value={selectedStaffId || "any"} onValueChange={(value) => setSelectedStaffId(value === "any" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any available staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any available staff</SelectItem>
                    {staff.filter(s => s.active).map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes (Optional)</Label>
                <Input
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add any notes about this appointment"
                />
              </div>

              {/* Client inspiration media (from booking) */}
              {editingInspirationMedia.length > 0 && (
                <div className="space-y-2">
                  <Label>Client inspiration</Label>
                  <div className="flex flex-wrap gap-2">
                    {editingInspirationMedia.map((m, i) => {
                      const publicUrl = supabase.storage.from("appointment-inspiration").getPublicUrl(m.file_path).data.publicUrl;
                      return m.media_type === "image" ? (
                        <a
                          key={i}
                          href={publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="block rounded-lg border border-border overflow-hidden w-28 h-28 sm:w-32 sm:h-32 shrink-0 hover:opacity-90"
                        >
                          <img src={publicUrl} alt="" className="w-full h-full object-cover" />
                        </a>
                      ) : (
                        <a
                          key={i}
                          href={publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-center rounded-lg border border-border w-28 h-28 sm:w-32 sm:h-32 shrink-0 bg-muted text-muted-foreground text-xs hover:bg-muted/80"
                        >
                          Video
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Customer Information */}
            <div className="space-y-5">
              {/* Customer Name */}
              <div className="space-y-2">
                <Label htmlFor="edit-customer-name">{customerLabel} Name *</Label>
                <Input
                  id="edit-customer-name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                />
              </div>

              {/* Customer Phone */}
              <div className="space-y-2">
                <Label htmlFor="edit-customer-phone">Customer Phone *</Label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+233">GH +233</SelectItem>
                      <SelectItem value="+1">US +1</SelectItem>
                      <SelectItem value="+44">UK +44</SelectItem>
                      <SelectItem value="+234">NG +234</SelectItem>
                      <SelectItem value="+254">KE +254</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="edit-customer-phone"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="Phone number"
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Customer Email */}
              <div className="space-y-2">
                <Label htmlFor="edit-customer-email">Customer Email (Optional)</Label>
                <Input
                  id="edit-customer-email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
            </div>
          </div>
          </div>

          <DialogFooter className="px-6 pb-6 pt-4 border-t flex-shrink-0 gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setEditingAppointment(null);
                resetAppointmentForm();
              }}
              disabled={isUpdating}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateAppointment} disabled={isUpdating} className="flex-1">
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Appointment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Appointment Details Dialog (read-only) */}
      <Dialog
        open={isViewDetailsOpen}
        onOpenChange={(open) => {
          setIsViewDetailsOpen(open);
          if (!open) {
            setViewingAppointment(null);
            setViewingInspirationMedia([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
            <DialogDescription>View appointment and client inspiration</DialogDescription>
          </DialogHeader>
          {viewingAppointment && (
            <div className="space-y-4 py-2 overflow-y-auto">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Service</Label>
                <p className="text-sm font-medium">{viewingAppointment.service?.name}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Date & Time</Label>
                <p className="text-sm">
                  {format(new Date(viewingAppointment.start_datetime), "EEE, MMM d, yyyy")} •{" "}
                  {format(new Date(viewingAppointment.start_datetime), "h:mm a")} -{" "}
                  {format(new Date(viewingAppointment.end_datetime), "h:mm a")}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Service provider</Label>
                <p className="text-sm font-medium">
                  {(viewingAppointment as any).staffMember?.name || "Not assigned"}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Customer</Label>
                <p className="text-sm font-medium">{viewingAppointment.customer?.name || "—"}</p>
                {viewingAppointment.customer?.phone && (
                  <p className="text-sm text-muted-foreground">{viewingAppointment.customer.phone}</p>
                )}
                {viewingAppointment.customer?.email && (
                  <p className="text-sm text-muted-foreground">{viewingAppointment.customer.email}</p>
                )}
              </div>
              {(viewingAppointment as any).confirmation_code && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Booking code</Label>
                  <p className="text-sm font-mono font-medium">{(viewingAppointment as any).confirmation_code}</p>
                  <p className="text-xs text-muted-foreground">Customer can use this code to pay remaining balance online.</p>
                </div>
              )}
              {viewingAppointment.notes && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm whitespace-pre-wrap">{viewingAppointment.notes}</p>
                </div>
              )}
              {viewingInspirationMedia.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Client inspiration</Label>
                  <div className="flex flex-wrap gap-2">
                    {viewingInspirationMedia.map((m, i) => {
                      const publicUrl = supabase.storage.from("appointment-inspiration").getPublicUrl(m.file_path).data.publicUrl;
                      return m.media_type === "image" ? (
                        <a
                          key={i}
                          href={publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="block rounded-lg border border-border overflow-hidden w-28 h-28 sm:w-32 sm:h-32 shrink-0 hover:opacity-90"
                        >
                          <img src={publicUrl} alt="" className="w-full h-full object-cover" />
                        </a>
                      ) : (
                        <a
                          key={i}
                          href={publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-center rounded-lg border border-border w-28 h-28 sm:w-32 sm:h-32 shrink-0 bg-muted text-muted-foreground text-xs hover:bg-muted/80"
                        >
                          Video
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDetailsOpen(false)}>
              Close
            </Button>
            {viewingAppointment && (
              <Button
                onClick={() => {
                  setIsViewDetailsOpen(false);
                  openEditDialog(viewingAppointment);
                }}
              >
                Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
